/**
 * Shared modal engine.
 *
 * One behaviour core, styling supplied entirely by a variant class (modal--detail,
 * modal--media, modal--notice, ...). Adding a new look must never require touching this file.
 *
 * Built on the native <dialog> element, so focus trapping, Esc and top-layer stacking come from
 * the browser rather than from library code. The dialog itself is a full-viewport flex container
 * carrying the dim overlay; the visible card is `.modal__panel` inside it. That is deliberate:
 * it gives each variant complete styling freedom (::backdrop is far more limited), and it makes
 * "clicked outside the panel" a reliable `event.target === dialog` check.
 *
 * Markup contract:
 *   <a href="#some-id" data-modal="#some-id">Open</a>
 *   <dialog id="some-id" class="modal modal--detail">
 *     <div class="modal__panel"> ... <button data-modal-close></button> ... </div>
 *   </dialog>
 *
 * The trigger stays a real anchor pointing at real in-page content, so when <dialog> is
 * unsupported nothing is intercepted and the link still does something sensible.
 *
 * Options (per trigger, via data attributes):
 *   data-modal-disable-below="700"  Leave the link alone below this viewport width. Used by the
 *                                   video popups to preserve today's behaviour of sending mobile
 *                                   users straight to YouTube; omit it to always open.
 */

(function () {
	'use strict';

	// Both trigger flavours: [data-modal] points at an existing dialog, [data-modal-video]
	// makes the engine build a media dialog around an embed URL.
	var TRIGGER_SELECTOR = '[data-modal], [data-modal-video]';
	var CLOSE_SELECTOR = '[data-modal-close]';
	var LOCK_CLASS = 'modal-open';

	var FOCUSABLE = [
		'a[href]',
		'button:not([disabled])',
		'input:not([disabled]):not([type="hidden"])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'[tabindex]:not([tabindex="-1"])'
	].join( ',' );

	interface EsrbModalApi {
		isSupported(): boolean;
		open( target: string | HTMLDialogElement, trigger?: HTMLElement | null ): boolean;
		close( dialog?: HTMLDialogElement | null ): void;
		/** Build a throwaway dialog around `content`; it removes itself when closed. */
		create( content: Node, variant?: string ): HTMLDialogElement;
		/** Close button markup shared by the dynamically built variants. */
		closeButton(): HTMLButtonElement;
	}

	function isSupported(): boolean {
		return typeof window.HTMLDialogElement === 'function'
			&& typeof window.HTMLDialogElement.prototype.showModal === 'function';
	}

	/* ---------------------------------------------------------------------- scroll lock ---- */

	// Counted, so nested or rapidly re-opened dialogs cannot leave the page permanently locked.
	var lockCount = 0;
	var savedPaddingRight = '';

	function lockScroll(): void {
		lockCount += 1;

		if ( lockCount > 1 ) {
			return;
		}

		var root = document.documentElement;
		// Compensate for the scrollbar the lock removes, otherwise the page shifts sideways.
		var scrollbarWidth = window.innerWidth - root.clientWidth;

		savedPaddingRight = root.style.paddingRight;

		if ( scrollbarWidth > 0 ) {
			root.style.paddingRight = String( scrollbarWidth ) + 'px';
		}

		// overflow:hidden rather than position:fixed - the latter loses the scroll position and
		// would jump the user to the top, which matters on the long search filter page.
		root.classList.add( LOCK_CLASS );
	}

	function unlockScroll(): void {
		lockCount -= 1;

		if ( lockCount > 0 ) {
			return;
		}

		lockCount = 0;

		var root = document.documentElement;

		root.classList.remove( LOCK_CLASS );
		root.style.paddingRight = savedPaddingRight;
	}

	/* --------------------------------------------------------------------------- focus ----- */

	var lastTrigger: HTMLElement | null = null;

	function focusInside( dialog: HTMLDialogElement ): void {
		// Prefer an explicit [autofocus], then the panel itself, and only then the first
		// control. Focusing the panel announces the dialog without visibly focusing a button.
		var preferred = dialog.querySelector<HTMLElement>( '[autofocus]' );
		var panel = dialog.querySelector<HTMLElement>( '.modal__panel[tabindex]' );
		var target = preferred || panel || dialog.querySelector<HTMLElement>( FOCUSABLE ) || dialog;

		try {
			target.focus();
		} catch ( error ) {
			// Focusing can throw on detached or hidden nodes; not worth failing the open for.
		}
	}

	/* -------------------------------------------------------------------- open / close ----- */

	function resolve( target: string | HTMLDialogElement ): HTMLDialogElement | null {
		if ( typeof target !== 'string' ) {
			return target;
		}

		var found = document.querySelector( target );

		return found instanceof HTMLDialogElement ? found : null;
	}

	function close( dialog?: HTMLDialogElement | null ): void {
		var target = dialog || document.querySelector<HTMLDialogElement>( 'dialog.modal[open]' );

		if ( target && target.open ) {
			target.close();
		}
	}

	function open( target: string | HTMLDialogElement, trigger?: HTMLElement | null ): boolean {
		if ( !isSupported() ) {
			return false;
		}

		var dialog = resolve( target );

		if ( !dialog || dialog.open ) {
			return false;
		}

		lastTrigger = trigger || null;

		dialog.showModal();
		lockScroll();
		focusInside( dialog );

		return true;
	}

	/* ----------------------------------------------------------------- dynamic modals ------ */

	function closeLabel(): string {
		// Mirrors how fullscreen_notice.js already picks its button copy.
		var esrb = ( window as unknown as { ESRB?: { spanish?: boolean } } ).ESRB;

		return esrb && esrb.spanish ? 'Cerrar' : 'Close';
	}

	function closeButton(): HTMLButtonElement {
		var button = document.createElement( 'button' );

		button.type = 'button';
		button.className = 'modal__close';
		button.setAttribute( 'data-modal-close', '' );
		button.setAttribute( 'aria-label', closeLabel() );
		button.innerHTML = '&times;';

		return button;
	}

	/**
	 * Dialog built at click time rather than rendered by PHP - used by the video and exit-notice
	 * consumers, which compose their content on the fly. Marked transient so it removes itself
	 * on close and cannot accumulate in the DOM.
	 */
	function create( content: Node, variant?: string ): HTMLDialogElement {
		var dialog = document.createElement( 'dialog' );

		dialog.className = variant ? 'modal ' + variant : 'modal';
		dialog.dataset.modalTransient = '1';

		var panel = document.createElement( 'div' );

		panel.className = 'modal__panel';
		panel.setAttribute( 'tabindex', '-1' );
		panel.appendChild( content );

		dialog.appendChild( panel );
		document.body.appendChild( dialog );
		bindDialog( dialog );

		return dialog;
	}

	/* ------------------------------------------------------------------------ wiring ------- */

	function bindDialog( dialog: HTMLDialogElement ): void {
		if ( dialog.dataset.modalBound === '1' ) {
			return;
		}

		dialog.dataset.modalBound = '1';

		// Clicking the overlay - i.e. the dialog itself rather than anything inside the panel.
		dialog.addEventListener( 'click', function ( event: MouseEvent ) {
			if ( event.target === dialog ) {
				close( dialog );
			}
		} );

		dialog.addEventListener( 'click', function ( event: MouseEvent ) {
			var origin = event.target;

			if ( origin instanceof Element && origin.closest( CLOSE_SELECTOR ) ) {
				event.preventDefault();
				close( dialog );
			}
		} );

		// Fires for Esc and for close() alike, so the lock and focus restore live in one place.
		dialog.addEventListener( 'close', function () {
			unlockScroll();

			if ( lastTrigger && document.contains( lastTrigger ) ) {
				try {
					lastTrigger.focus();
				} catch ( error ) {
					// See focusInside().
				}
			}

			lastTrigger = null;

			// Remove after focus has been handed back, never before.
			if ( dialog.dataset.modalTransient === '1' ) {
				dialog.remove();
			}
		} );
	}

	function onTriggerClick( event: MouseEvent ): void {
		var origin = event.target;

		if ( !( origin instanceof Element ) ) {
			return;
		}

		var trigger = origin.closest<HTMLElement>( TRIGGER_SELECTOR );

		if ( !trigger ) {
			return;
		}

		// Let modified clicks behave like normal link clicks.
		if ( event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0 ) {
			return;
		}

		var selector = trigger.getAttribute( 'data-modal' );
		var videoSrc = trigger.getAttribute( 'data-modal-video' );

		if ( !selector && !videoSrc ) {
			return;
		}

		var disableBelow = parseInt( trigger.getAttribute( 'data-modal-disable-below' ) || '0', 10 );

		if ( disableBelow > 0 && window.innerWidth < disableBelow ) {
			// Below the threshold the trigger stays an ordinary link (mobile goes to YouTube).
			return;
		}

		if ( videoSrc ) {
			var frame = document.createElement( 'iframe' );

			frame.src = videoSrc;
			frame.setAttribute( 'frameborder', '0' );
			frame.setAttribute( 'allow', 'autoplay; encrypted-media' );
			frame.setAttribute( 'allowfullscreen', 'true' );
			frame.setAttribute( 'title', trigger.getAttribute( 'title' ) || closeLabel() );

			var media = document.createElement( 'div' );

			media.className = 'modal__media';
			media.appendChild( frame );

			var frag = document.createDocumentFragment();

			frag.appendChild( media );
			frag.appendChild( closeButton() );

			event.preventDefault();
			open( create( frag, 'modal--media' ), trigger );

			return;
		}

		if ( !selector ) {
			return;
		}

		var dialog = resolve( selector );

		if ( !dialog ) {
			return;
		}

		bindDialog( dialog );

		if ( open( dialog, trigger ) ) {
			event.preventDefault();
		}
	}

	if ( isSupported() ) {
		// Delegated, so markup rendered after load (AJAX results, for instance) works too.
		document.addEventListener( 'click', onTriggerClick );
	}

	var api: EsrbModalApi = {
		isSupported: isSupported,
		open: function ( target, trigger ) {
			var dialog = resolve( target );

			if ( !dialog ) {
				return false;
			}

			bindDialog( dialog );

			return open( dialog, trigger );
		},
		close: close,
		create: create,
		closeButton: closeButton
	};

	( window as unknown as { EsrbModal?: EsrbModalApi } ).EsrbModal = api;
})();
